import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import SettingsDialog from '@/components/settings/SettingsDialog';
import { useAuth } from '@/components/auth/AuthProvider';
import type {
  SettingsSectionId,
  SettingsSectionRequest,
} from '@/components/settings/settingsTypes';

interface SettingsDialogContextValue {
  openSettings: (section?: SettingsSectionId) => void;
}

const SettingsDialogContext = createContext<SettingsDialogContextValue | null>(null);

export const useSettingsDialog = () => useContext(SettingsDialogContext);

export function SettingsDialogProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const accountId = user?.user_id ?? user?.id ?? user?.auth0Id ?? null;
  const [sectionRequest, setSectionRequest] = useState<SettingsSectionRequest | null>(null);
  const [removedAccountId, setRemovedAccountId] = useState<string | null>(null);
  if (removedAccountId && removedAccountId !== accountId) setRemovedAccountId(null);
  const restoreFocus = useRef<HTMLElement | null>(null);

  const openSettings = useCallback(
    (next: SettingsSectionId = 'general') => {
      if (!sectionRequest) {
        const active = document.activeElement;
        const fromMenu = active?.closest('.MuiDrawer-root, .MuiMenu-root');
        restoreFocus.current =
          (fromMenu &&
            document.querySelector<HTMLElement>('[data-settings-return-focus="account"]')) ||
          (active instanceof HTMLElement ? active : null);
      }
      setSectionRequest({
        section: removedAccountId === accountId && removedAccountId ? 'account' : next,
      });
    },
    [accountId, removedAccountId, sectionRequest]
  );

  const closeSettings = useCallback(() => {
    setSectionRequest(null);
    const target = restoreFocus.current;
    requestAnimationFrame(() => {
      if (target?.isConnected && !target.closest('[aria-hidden="true"]')) target.focus();
    });
  }, []);

  const value = useMemo(() => ({ openSettings }), [openSettings]);
  return (
    <SettingsDialogContext.Provider value={value}>
      {children}
      {sectionRequest && (
        <SettingsDialog
          open
          initialSection={sectionRequest.section}
          sectionRequest={sectionRequest}
          onClose={closeSettings}
          removedAccountId={removedAccountId === accountId ? removedAccountId : null}
          onAccountRemoved={setRemovedAccountId}
        />
      )}
    </SettingsDialogContext.Provider>
  );
}
